import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar as CalendarIcon, MapPin, Building2, User as UserIcon,
  Euro, TrendingUp, Pencil, Trash2, CheckCircle2, Circle, Inbox, ClipboardList,
  Target, Lightbulb, MessageSquare, FileText, Sparkles, Wrench, CheckCheck, Calendar as CalIcon2, Copy,
  Printer, Upload, Download, Paperclip, FolderOpen, UserCheck, Send, XCircle
} from "lucide-react";
import { CHECKPOINTS, DEMAND_SOURCES, PHASE_PALETTE, PHASE_TEXT_COLOR } from "@/lib/eventConstants";
import EventCommercialisationTab from "@/pages/EventCommercialisationTab";
import EventProductionTab from "@/pages/EventProductionTab";
import EventPostprodTab from "@/pages/EventPostprodTab";

const PHASE_BADGES = {
  commercialisation: "bg-primary/10 text-primary border-primary/40",
  production: "bg-accent text-accent-foreground border-accent",
  postprod: "bg-muted text-foreground border-border",
  cancelled: "bg-red-500/15 text-red-200 border-red-500/50",
  archived: "bg-muted text-muted-foreground border-border",
};
const PHASE_LABEL = {
  commercialisation: "Commercialisation",
  production: "Production",
  postprod: "Post-production",
  cancelled: "Événement annulé",
  archived: "Archivé",
};
const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAYS_FR = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
function longDate(iso) {
  if (!iso || iso.length < 10) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${DAYS_FR[d.getDay()]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

// Groupes = phases (renommées) — utilisés pour les flèches d'avancement.
const CHECKPOINT_GROUPS = ["Commercialisation", "Production", "Post-production"];
const GROUP_ICON = { "Commercialisation": FileText, "Production": ClipboardList, "Post-production": CheckCheck };

function Kpi({ label, value, sub, tone = "default", testId }) {
  const bg = {
    primary: "bg-primary/5 border-primary/30",
    accent: "bg-secondary/20 border-secondary/60",
    default: "bg-muted border-border/60",
    danger: "bg-destructive/5 border-destructive/40",
  }[tone] || "bg-muted";
  return (
    <div className={`rounded-2xl border p-4 ${bg} min-w-0`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className="font-brand text-xl md:text-2xl font-semibold mt-1 tabular-nums truncate" title={typeof value === "string" ? value : ""}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{sub}</div>}
    </div>
  );
}

// Flèche/chevron d'avancement — reprend la couleur de la phase avec un dégradé
// du plus clair au plus foncé selon l'ordre de l'étape dans la phase.
// Chevron d'avancement avec forme totalement fermée grâce à un overlay SVG.
// - État validé : polygone rempli d'un gradient de la palette de phase.
// - État à faire : polygone à contour pointillé bien visible.
function ChevronStep({ cp, indexInGroup, groupSize, palette, textColors, state, onToggle, isCurrent, isJustDone }) {
  const done = !!state?.done;
  const activeIdx = Math.min(indexInGroup, palette.length - 1);
  const c1 = palette[activeIdx];
  const c2 = palette[Math.min(activeIdx + 1, palette.length - 1)];
  const borderColor = palette[Math.min(indexInGroup, palette.length - 1)];
  const color = done ? (textColors?.[activeIdx] || "#fff") : "#374151";
  const isFirst = indexInGroup === 0;
  const isLast = indexInGroup === groupSize - 1;
  // viewBox 300x92 stretché avec preserveAspectRatio="none".
  // Le stroke est constant en pixels grâce à vector-effect="non-scaling-stroke".
  const P = 22;           // profondeur du biseau en unités svg
  const H = 92, W = 300;
  const points = (() => {
    if (isFirst && isLast) return `0,0 ${W},0 ${W},${H} 0,${H}`;
    if (isFirst) return `0,0 ${W - P},0 ${W},${H / 2} ${W - P},${H} 0,${H}`;
    if (isLast)  return `0,0 ${W},0 ${W},${H} 0,${H} ${P},${H / 2}`;
    return `0,0 ${W - P},0 ${W},${H / 2} ${W - P},${H} 0,${H} ${P},${H / 2}`;
  })();
  const gradId = `grad-${cp.key}`;
  const animCls = isJustDone
    ? "chevron-just-done"
    : (isCurrent && !done ? "chevron-current-pulse" : "");
  return (
    <button
      type="button"
      onClick={() => onToggle(cp.key, !done)}
      data-testid={`checkpoint-${cp.key}`}
      className={`relative flex-1 min-w-[170px] h-[92px] p-0 text-center transition-all duration-500 active:scale-[0.98] ${animCls}`}
      style={{ color, background: "transparent", border: "none" }}
      title={cp.label}
    >
      <svg className="absolute inset-0 w-full h-full pointer-events-none"
           preserveAspectRatio="none"
           viewBox={`0 0 ${W} ${H}`}>
        {done && (
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={c1} />
              <stop offset="100%" stopColor={c2} />
            </linearGradient>
          </defs>
        )}
        <polygon
          points={points}
          fill={done ? `url(#${gradId})` : "rgba(255,255,255,0.55)"}
          stroke={borderColor}
          strokeWidth={done ? 0 : 2.5}
          strokeDasharray={done ? "" : "7 4"}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {isJustDone && (
        <span className="chevron-sparkle" aria-hidden="true">
          <Sparkles className="h-4 w-4" />
        </span>
      )}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6">
        <div className="text-[10px] uppercase tracking-[0.15em] font-black opacity-70 leading-none">Étape</div>
        <div className={`font-brand ${done ? "text-2xl" : "text-xl"} font-black leading-none tabular-nums mt-0.5`}>{String(indexInGroup + 1).padStart(2, "0")}</div>
        <div className="text-[11px] font-semibold leading-tight mt-1.5 line-clamp-2 opacity-95">{cp.label}</div>
        {done && state?.date && (
          <div className="text-[10px] tabular-nums opacity-80 mt-1">{longDate(state.date)}</div>
        )}
      </div>
    </button>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "demande";
  const setActiveTab = (v) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", v);
    setSearchParams(p, { replace: true });
  };
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [justDoneKey, setJustDoneKey] = useState(null);
  const [editDemandeOpen, setEditDemandeOpen] = useState(false);
  const [demandeForm, setDemandeForm] = useState(null);
  const [demandeSaving, setDemandeSaving] = useState(false);
  const [aaeUsers, setAaeUsers] = useState([]);
  useEffect(() => {
    api.get("/users/lookup").then(({ data }) => setAaeUsers(data || [])).catch(() => setAaeUsers([]));
  }, []);
  const openEditDemande = () => {
    if (!event) return;
    setDemandeForm({
      name: event.name || "",
      client: event.client || "",
      start_date: event.start_date || "",
      end_date: event.end_date || "",
      horaire: event.horaire || "",
      horaire_end: event.horaire_end || "",
      location: event.location || "",
      space: event.space || "",
      address: event.address || "",
      budget: event.budget ?? "",
      budget_type: event.budget_type || "HT",
      budget_comment: event.budget_comment || "",
      referent_id: event.referent_id || "",
      description: event.description || "",
      demande_source: event.demande?.source || "",
      demande_received_by_id: event.demande?.received_by_id || "",
      demande_decision_date: event.demande?.decision_date || "",
      demande_decision_comment: event.demande?.decision_comment || "",
      demande_quote_request_date: event.demande?.quote_request_date || "",
      demande_quote_request_comment: event.demande?.quote_request_comment || "",
      brief_contact_first_name: event.brief?.contact_first_name || "",
      brief_contact_last_name: event.brief?.contact_last_name || "",
      brief_entreprise: event.brief?.entreprise || "",
      brief_position: event.brief?.position || "",
      brief_email: event.brief?.email || "",
      brief_phone: event.brief?.phone || "",
    });
    setEditDemandeOpen(true);
  };
  const saveDemande = async () => {
    if (!demandeForm) return;
    if (!demandeForm.name.trim()) { toast.error("Le nom de l'événement est obligatoire"); return; }
    setDemandeSaving(true);
    try {
      const payload = {
        name: demandeForm.name,
        client: demandeForm.client,
        start_date: demandeForm.start_date || null,
        end_date: demandeForm.end_date || null,
        horaire: demandeForm.horaire || null,
        horaire_end: demandeForm.horaire_end || null,
        location: demandeForm.location || null,
        space: demandeForm.space || null,
        address: demandeForm.address || null,
        budget: demandeForm.budget === "" || demandeForm.budget === null ? 0 : parseFloat(demandeForm.budget),
        budget_type: demandeForm.budget_type || "HT",
        budget_comment: demandeForm.budget_comment || "",
        referent_id: demandeForm.referent_id || null,
        description: demandeForm.description || "",
        demande: {
          ...(event.demande || {}),
          source: demandeForm.demande_source || null,
          received_by_id: demandeForm.demande_received_by_id || null,
          decision_date: demandeForm.demande_decision_date || null,
          decision_comment: demandeForm.demande_decision_comment || "",
          quote_request_date: demandeForm.demande_quote_request_date || null,
          quote_request_comment: demandeForm.demande_quote_request_comment || "",
        },
        brief: {
          ...(event.brief || {}),
          contact_first_name: demandeForm.brief_contact_first_name || "",
          contact_last_name: demandeForm.brief_contact_last_name || "",
          contact_full_name: [demandeForm.brief_contact_first_name, demandeForm.brief_contact_last_name].filter(Boolean).join(" ").trim(),
          entreprise: demandeForm.brief_entreprise || "",
          position: demandeForm.brief_position || "",
          email: demandeForm.brief_email || "",
          phone: demandeForm.brief_phone || "",
        },
      };
      await api.patch(`/events/${id}`, payload);
      toast.success("Demande mise à jour");
      setEditDemandeOpen(false);
      await load(true);
    } catch (e) { toast.error(extractError(e)); }
    finally { setDemandeSaving(false); }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/events/${id}`);
      setEvent(data);
    } catch (e) {
      toast.error(extractError(e));
      if (!silent) navigate("/", { replace: true });
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const progressDone = useMemo(() => {
    const cp = event?.checkpoints || {};
    return CHECKPOINTS.filter(c => cp[c.key]?.done).length;
  }, [event]);
  const progressPct = Math.round((progressDone / CHECKPOINTS.length) * 100);

  const toggleCheckpoint = async (key, done) => {
    try {
      const { data } = await api.patch(`/events/${id}/checkpoint`, { key, done });
      setEvent(ev => ({ ...ev, checkpoints: data.checkpoints }));
      if (done) {
        setJustDoneKey(key);
        setTimeout(() => setJustDoneKey(null), 1400);
      }
      // Refresh silencieux : pas de spinner, donc pas de saut au top de page
      setTimeout(() => load(true), 200);
    } catch (e) { toast.error(extractError(e)); }
  };

  const removeEvent = async () => {
    if (!confirm(`Supprimer "${event.name}" ?`)) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Événement supprimé");
      navigate("/");
    } catch (e) { toast.error(extractError(e)); }
  };

  const duplicateEvent = async () => {
    if (!confirm(`Dupliquer "${event.name}" ?\n\nLe brief, le ciblage, les idées, la roadmap, les véhicules et les demandes prestataires/artistes seront copiés. Les dates, checkpoints, offre client et post-production seront réinitialisés.`)) return;
    try {
      const { data } = await api.post(`/events/${id}/duplicate`);
      toast.success("Événement dupliqué — vous êtes sur la copie");
      navigate(`/evenements/${data.id}`);
    } catch (e) { toast.error(extractError(e)); }
  };

  const printDemandePdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/events/${id}/demande-pdf`;
    window.open(url, "_blank", "noopener");
  };

  if (loading || !event) {
    return <div className="p-8 text-center text-muted-foreground">Chargement…</div>;
  }

  const sourceLabel = DEMAND_SOURCES.find(s => s.v === event.demande?.source)?.label;

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      {/* Header retour */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="rounded-full" onClick={() => navigate("/")} data-testid="back-to-kanban">
          <ArrowLeft className="h-4 w-4 mr-2" /> Retour au Kanban
        </Button>
        <div className="flex items-center gap-2">
          <Badge className={`${PHASE_BADGES[event.phase] || ""} rounded-full border max-w-[160px] truncate whitespace-nowrap`} title={PHASE_LABEL[event.phase]}>{PHASE_LABEL[event.phase]}</Badge>
          <Button variant="outline" size="sm" className="rounded-full border-primary/40 text-primary hover:bg-primary/5" onClick={duplicateEvent} data-testid="duplicate-event-btn">
            <Copy className="h-4 w-4 mr-2" /> Dupliquer
          </Button>
          <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={removeEvent} data-testid="delete-event-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Supprimer
          </Button>
        </div>
      </div>

      {/* Bandeau top : Client · Nom · Date */}
      <div className="rounded-3xl bg-card border p-6 md:p-8 shadow-warm">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Client</div>
        <div className="font-brand text-3xl md:text-4xl leading-tight tracking-tight">
          {event.client || <span className="text-muted-foreground italic">Client à définir</span>}
        </div>
        <div className="font-display text-lg md:text-xl text-foreground/80 mt-3">{event.name}</div>
        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
          <div className="flex items-center gap-1.5 text-primary font-medium tabular-nums">
            <CalendarIcon className="h-4 w-4" />
            {event.start_date ? longDate(event.start_date) : "Date à définir"}
            {event.end_date && event.end_date !== event.start_date && <> → {longDate(event.end_date)}</>}
          </div>
          {(event.horaire || event.horaire_end) && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <CalIcon2 className="h-4 w-4 text-primary" />
              {event.horaire || "—"}
              {event.horaire_end && <> → {event.horaire_end}</>}
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" /> {event.location}
              {event.space && <span className="text-primary/90">· {event.space}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Tuiles KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi
          label="Statut"
          value={event.status_info?.label || PHASE_LABEL[event.phase] || "—"}
          sub={event.status_info?.precision || `${progressDone}/${CHECKPOINTS.length} étapes validées`}
          tone="accent"
          testId="kpi-statut"
        />
        <Kpi
          label="Budget"
          value={`${Number(event.budget_effective ?? event.budget ?? 0).toLocaleString("fr-FR")} €`}
          sub={
            event.budget_source === "offer_accepted"
              ? "Devis validé client"
              : event.budget_source === "offer_sent"
                ? "Proposition en cours"
                : (Number(event.budget || 0) > 0 ? "Budget déclaré" : "Non déclaré")
          }
          tone={event.budget_source === "offer_accepted" ? "primary" : "default"}
          testId="kpi-budget"
        />
        <Kpi
          label="Marge"
          value={`${(event.margin_effective ?? 0).toLocaleString("fr-FR")} €`}
          sub={event.margin_source === "manual"
            ? "Saisie manuellement"
            : `Budget − offres reçues (${Number(event.engaged_total || 0).toLocaleString("fr-FR")} €)`}
          tone={event.margin_effective >= 0 ? "primary" : "danger"}
          testId="kpi-margin"
        />
        <Kpi
          label="Dépenses engagées"
          value={`${Number(event.deposits_paid_total || 0).toLocaleString("fr-FR")} €`}
          sub={
            Number(event.remaining_to_pay || 0) > 0
              ? `Reste ${Number(event.remaining_to_pay).toLocaleString("fr-FR")} € à payer`
              : (Number(event.validated_total || 0) > 0 ? "Tout est payé ✓" : "Aucune offre validée")
          }
          tone={Number(event.deposits_paid_total || 0) > 0 ? "accent" : "default"}
          testId="kpi-engagees"
        />
        <Kpi
          label={(event.phase === "commercialisation" || event.phase === "cancelled") ? "Chargé de commercialisation" : "Chargé de projet"}
          value={
            (event.phase === "commercialisation" || event.phase === "cancelled")
              ? (event.commercial_lead_name || event.referent_name || "—")
              : (event.project_lead_name || event.referent_name || "—")
          }
          sub={
            (event.phase === "commercialisation" || event.phase === "cancelled")
              ? (event.commercial_lead_name ? "Commerce AAE" : "À désigner en Commercialisation")
              : (event.project_lead_name ? "Projet AAE" : "À désigner en Production")
          }
          tone="default"
          testId="kpi-referent"
        />
      </div>

      {/* Stepper 12 étapes */}
      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-primary" /> Avancement
              </div>
              <div className="font-brand text-2xl mt-1">{progressPct}% <span className="text-sm font-normal text-muted-foreground">— {progressDone}/{CHECKPOINTS.length} étapes</span></div>
            </div>
            <div className="flex-1 max-w-md ml-auto">
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          </div>
          <div className="space-y-6">
            {CHECKPOINT_GROUPS.map(group => {
              const items = CHECKPOINTS.filter(c => c.group === group);
              const Icon = GROUP_ICON[group] || FileText;
              const palette = PHASE_PALETTE[group] || ["#e5e7eb"];
              const textColors = PHASE_TEXT_COLOR[group] || ["#000"];
              return (
                <div key={group} data-testid={`phase-group-${group}`}>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-2">
                    <Icon className="h-3 w-3 text-primary" /> {group}
                    <span className="text-muted-foreground/60">· {items.filter(c => event.checkpoints?.[c.key]?.done).length}/{items.length}</span>
                  </div>
                  <div className="flex flex-wrap items-stretch gap-1">
                    {items.map((cp, idx) => (
                      <ChevronStep
                        key={cp.key}
                        cp={cp}
                        indexInGroup={idx}
                        groupSize={items.length}
                        palette={palette}
                        textColors={textColors}
                        state={event.checkpoints?.[cp.key]}
                        onToggle={toggleCheckpoint}
                        isCurrent={event.status_info?.key === cp.key}
                        isJustDone={justDoneKey === cp.key}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-muted">
          <TabsTrigger value="demande" data-testid="tab-demande"><Inbox className="h-4 w-4 mr-1" /> Demande</TabsTrigger>
          <TabsTrigger value="commerc" data-testid="tab-commerc"><FileText className="h-4 w-4 mr-1" /> Commercialisation</TabsTrigger>
          <TabsTrigger value="production" data-testid="tab-production"><Wrench className="h-4 w-4 mr-1" /> Production</TabsTrigger>
          <TabsTrigger value="postprod" data-testid="tab-postprod"><CheckCheck className="h-4 w-4 mr-1" /> Post-production</TabsTrigger>
        </TabsList>

        {/* ---- Demande ---- */}
        <TabsContent value="demande" className="mt-4 space-y-4">
          {/* Barre d'actions */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-gradient-to-r from-primary/5 to-accent/20 border border-border/60 px-4 py-3">
            <div className="text-sm font-semibold flex items-center gap-2">
              <Inbox className="h-4 w-4 text-primary" /> Récapitulatif de la demande
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-primary/40 text-primary hover:bg-primary/5"
                onClick={openEditDemande}
                data-testid="edit-demande-btn"
              >
                <Pencil className="h-4 w-4 mr-2" /> Modifier la demande
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-primary/40 text-primary hover:bg-primary/5"
                onClick={printDemandePdf}
                data-testid="print-demande-pdf-btn"
              >
                <Printer className="h-4 w-4 mr-2" /> Imprimer la demande (PDF)
              </Button>
            </div>
          </div>

          {/* Checklist administrative — feux vert / rouge */}
          <Card className="border-border/60 shadow-warm" data-testid="admin-checklist-card">
            <CardContent className="p-5 md:p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
                <ClipboardList className="h-3 w-3 text-primary" /> Checklist administrative — pré-requis à la proposition
              </div>
              <div className="space-y-2">
                {ADMIN_CHECKLIST.map(item => (
                  <AdminChecklistRow
                    key={item.key}
                    item={item}
                    state={event.checkpoints?.[item.key]}
                    onToggle={toggleCheckpoint}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Informations événement & budget */}
          <Card className="border-border/60 shadow-warm">
            <CardContent className="p-5 md:p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
                <CalendarIcon className="h-3 w-3 text-primary" /> Informations événement & budget
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Row label="Nom de l'événement" value={event.name} />
                <Row label="Client" value={event.client} />
                <Row label="Dates" value={
                  event.start_date
                    ? (event.end_date && event.end_date !== event.start_date
                        ? `${longDate(event.start_date)} → ${longDate(event.end_date)}`
                        : longDate(event.start_date))
                    : "—"
                } />
                <Row label="Horaire" value={
                  [event.horaire, event.horaire_end].filter(Boolean).join(" → ") || null
                } />
                <Row label="Adresse" value={event.address} full={!event.location} />
                {event.location && <Row label="Lieu" value={
                  event.space ? `${event.location} · ${event.space}` : event.location
                } />}
                <Row label={`Budget ${event.budget_type || "HT"}`} value={
                  event.budget ? `${Number(event.budget).toLocaleString("fr-FR")} €` : "—"
                } />
                {event.budget_comment && <Row label="Commentaires budget" value={event.budget_comment} full multiline />}
                <Row label="Chargé de commercialisation" value={event.commercial_lead_name || event.referent_name} />
                {(event.description || event.infos_supp) && (
                  <Row label="Description / infos" value={event.description || event.infos_supp} full multiline />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 shadow-warm">
            <CardContent className="p-5 md:p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
                <Inbox className="h-3 w-3 text-primary" /> Origine de la demande
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Reçue par</div>
                  <div className="font-medium mt-1 flex items-center gap-1.5"><UserIcon className="h-3.5 w-3.5 text-muted-foreground" /> {event.demande?.received_by_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Origine</div>
                  <div className="font-medium mt-1">{sourceLabel || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Prise de décision</div>
                  <div className="font-medium mt-1 tabular-nums">{event.demande?.decision_date ? longDate(event.demande.decision_date) : "—"}</div>
                  {event.demande?.decision_comment && <div className="text-xs text-muted-foreground italic mt-0.5">« {event.demande.decision_comment} »</div>}
                </div>
                <div className="md:col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Demande de remise du devis</div>
                  <div className="font-medium mt-1 tabular-nums">{event.demande?.quote_request_date ? longDate(event.demande.quote_request_date) : "—"}</div>
                  {event.demande?.quote_request_comment && <div className="text-xs text-muted-foreground italic mt-0.5">« {event.demande.quote_request_comment} »</div>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents joints — plans, photos, brief, ... */}
          <DocumentsCard event={event} reload={load} />

          {/* Brief client */}
          {event.brief && (
            <Card className="border-border/60 shadow-warm">
              <CardContent className="p-5 md:p-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
                  <Building2 className="h-3 w-3 text-primary" /> Brief client
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Row label="Prénom du contact" value={event.brief?.contact_first_name} />
                  <Row label="Nom du contact" value={event.brief?.contact_last_name} />
                  {!event.brief?.contact_first_name && !event.brief?.contact_last_name && event.brief?.contact_full_name && (
                    <Row label="Contact" value={event.brief?.contact_full_name} full />
                  )}
                  <Row label="Entreprise" value={event.brief?.entreprise || event.client} />
                  <Row label="Poste" value={event.brief?.position} />
                  <Row label="Email" value={event.brief?.email} />
                  <Row label="Téléphone" value={event.brief?.phone} />
                  {event.brief?.brief_other && <Row label="Autres" value={event.brief?.brief_other} full />}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ciblage */}
          {event.targeting && (event.targeting.for_who || event.targeting.age || event.targeting.purpose || event.targeting.gender || event.targeting.before_after || event.targeting.previous_events || event.targeting.other) && (
            <Card className="border-border/60 shadow-warm">
              <CardContent className="p-5 md:p-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
                  <Target className="h-3 w-3 text-primary" /> Ciblage
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Row label="Pour qui" value={event.targeting.for_who} />
                  <Row label="Âge" value={event.targeting.age} />
                  <Row label="Genre" value={event.targeting.gender} />
                  <Row label="But" value={event.targeting.purpose} />
                  {event.targeting.before_after && <Row label="Avant / après" value={event.targeting.before_after} full />}
                  {event.targeting.previous_events && <Row label="Événements précédents" value={event.targeting.previous_events} full />}
                  {event.targeting.other && <Row label="Autres" value={event.targeting.other} full />}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Idées */}
          {event.ideas && (event.ideas.wants || event.ideas.dont_wants || event.ideas.other) && (
            <Card className="border-border/60 shadow-warm">
              <CardContent className="p-5 md:p-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
                  <Lightbulb className="h-3 w-3 text-primary" /> Recueil idées client
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Row label="Ce qu'il aimerait" value={event.ideas.wants} multiline />
                  <Row label="Ce qu'il ne veut pas" value={event.ideas.dont_wants} multiline />
                  {event.ideas.other && <Row label="Autres" value={event.ideas.other} full />}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Commentaires */}
          {(event.infos_supp || event.description) && (
            <Card className="border-border/60 shadow-warm">
              <CardContent className="p-5 md:p-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
                  <MessageSquare className="h-3 w-3 text-primary" /> Commentaires
                </div>
                <div className="text-sm whitespace-pre-wrap">{event.infos_supp || event.description}</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---- Commercialisation ---- */}
        <TabsContent value="commerc" className="mt-4">
          <EventCommercialisationTab event={event} reload={load} />
        </TabsContent>

        {/* ---- Production ---- */}
        <TabsContent value="production" className="mt-4">
          <EventProductionTab event={event} reload={load} />
        </TabsContent>

        {/* ---- Post-production ---- */}
        <TabsContent value="postprod" className="mt-4">
          <EventPostprodTab event={event} reload={load} />
        </TabsContent>
      </Tabs>

      {/* Dialog : Modifier la demande — inline sur la page détail */}
      <Dialog open={editDemandeOpen} onOpenChange={setEditDemandeOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-demande-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la demande</DialogTitle>
          </DialogHeader>
          {demandeForm && (
            <div className="space-y-6 py-2">
              {/* Bloc : Événement */}
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Événement</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Nom de l'événement *</Label>
                    <Input value={demandeForm.name} onChange={e => setDemandeForm(f => ({ ...f, name: e.target.value }))} data-testid="ed-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Client</Label>
                    <Input value={demandeForm.client} onChange={e => setDemandeForm(f => ({ ...f, client: e.target.value }))} data-testid="ed-client" />
                  </div>
                  <div>
                    <Label className="text-xs">Chargé de commercialisation</Label>
                    <Select value={demandeForm.referent_id || "none"} onValueChange={v => setDemandeForm(f => ({ ...f, referent_id: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid="ed-referent"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {aaeUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date de début</Label>
                    <Input type="date" value={demandeForm.start_date} onChange={e => setDemandeForm(f => ({ ...f, start_date: e.target.value }))} data-testid="ed-start" />
                  </div>
                  <div>
                    <Label className="text-xs">Date de fin</Label>
                    <Input type="date" value={demandeForm.end_date} onChange={e => setDemandeForm(f => ({ ...f, end_date: e.target.value }))} data-testid="ed-end" />
                  </div>
                  <div>
                    <Label className="text-xs">Horaire début</Label>
                    <Input type="time" value={demandeForm.horaire} onChange={e => setDemandeForm(f => ({ ...f, horaire: e.target.value }))} data-testid="ed-horaire" />
                  </div>
                  <div>
                    <Label className="text-xs">Horaire fin</Label>
                    <Input type="time" value={demandeForm.horaire_end} onChange={e => setDemandeForm(f => ({ ...f, horaire_end: e.target.value }))} data-testid="ed-horaire-end" />
                  </div>
                  <div>
                    <Label className="text-xs">Lieu</Label>
                    <Input value={demandeForm.location} onChange={e => setDemandeForm(f => ({ ...f, location: e.target.value }))} data-testid="ed-location" />
                  </div>
                  <div>
                    <Label className="text-xs">Espace / Salle</Label>
                    <Input value={demandeForm.space} onChange={e => setDemandeForm(f => ({ ...f, space: e.target.value }))} data-testid="ed-space" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Adresse complète</Label>
                    <Input value={demandeForm.address} onChange={e => setDemandeForm(f => ({ ...f, address: e.target.value }))} data-testid="ed-address" />
                  </div>
                </div>
              </div>

              {/* Bloc : Budget */}
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Budget</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Budget</Label>
                    <Input type="number" step="0.01" value={demandeForm.budget} onChange={e => setDemandeForm(f => ({ ...f, budget: e.target.value }))} data-testid="ed-budget" />
                  </div>
                  <div>
                    <Label className="text-xs">HT / TTC</Label>
                    <Select value={demandeForm.budget_type} onValueChange={v => setDemandeForm(f => ({ ...f, budget_type: v }))}>
                      <SelectTrigger data-testid="ed-budget-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HT">HT</SelectItem>
                        <SelectItem value="TTC">TTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-xs">Commentaires budget</Label>
                    <Textarea rows={2} value={demandeForm.budget_comment} onChange={e => setDemandeForm(f => ({ ...f, budget_comment: e.target.value }))} data-testid="ed-budget-comment" />
                  </div>
                </div>
              </div>

              {/* Bloc : Origine de la demande */}
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Origine de la demande</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Reçue par</Label>
                    <Select value={demandeForm.demande_received_by_id || "none"} onValueChange={v => setDemandeForm(f => ({ ...f, demande_received_by_id: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid="ed-received-by"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {aaeUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Origine</Label>
                    <Select value={demandeForm.demande_source || "none"} onValueChange={v => setDemandeForm(f => ({ ...f, demande_source: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid="ed-source"><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {DEMAND_SOURCES.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date de prise de décision</Label>
                    <Input type="date" value={demandeForm.demande_decision_date} onChange={e => setDemandeForm(f => ({ ...f, demande_decision_date: e.target.value }))} data-testid="ed-decision-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Commentaire décision</Label>
                    <Input value={demandeForm.demande_decision_comment} onChange={e => setDemandeForm(f => ({ ...f, demande_decision_comment: e.target.value }))} data-testid="ed-decision-comment" />
                  </div>
                  <div>
                    <Label className="text-xs">Date demande de devis</Label>
                    <Input type="date" value={demandeForm.demande_quote_request_date} onChange={e => setDemandeForm(f => ({ ...f, demande_quote_request_date: e.target.value }))} data-testid="ed-quote-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Commentaire demande de devis</Label>
                    <Input value={demandeForm.demande_quote_request_comment} onChange={e => setDemandeForm(f => ({ ...f, demande_quote_request_comment: e.target.value }))} data-testid="ed-quote-comment" />
                  </div>
                </div>
              </div>

              {/* Bloc : Contact / Brief */}
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Contact client</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Prénom du contact</Label>
                    <Input value={demandeForm.brief_contact_first_name} onChange={e => setDemandeForm(f => ({ ...f, brief_contact_first_name: e.target.value }))} data-testid="ed-contact-first" />
                  </div>
                  <div>
                    <Label className="text-xs">Nom du contact</Label>
                    <Input value={demandeForm.brief_contact_last_name} onChange={e => setDemandeForm(f => ({ ...f, brief_contact_last_name: e.target.value }))} data-testid="ed-contact-last" />
                  </div>
                  <div>
                    <Label className="text-xs">Entreprise</Label>
                    <Input value={demandeForm.brief_entreprise} onChange={e => setDemandeForm(f => ({ ...f, brief_entreprise: e.target.value }))} data-testid="ed-entreprise" />
                  </div>
                  <div>
                    <Label className="text-xs">Poste</Label>
                    <Input value={demandeForm.brief_position} onChange={e => setDemandeForm(f => ({ ...f, brief_position: e.target.value }))} data-testid="ed-position" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={demandeForm.brief_email} onChange={e => setDemandeForm(f => ({ ...f, brief_email: e.target.value }))} data-testid="ed-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Téléphone</Label>
                    <Input value={demandeForm.brief_phone} onChange={e => setDemandeForm(f => ({ ...f, brief_phone: e.target.value }))} data-testid="ed-phone" />
                  </div>
                </div>
              </div>

              {/* Bloc : Description */}
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Description / commentaires</div>
                <Textarea rows={4} value={demandeForm.description} onChange={e => setDemandeForm(f => ({ ...f, description: e.target.value }))} data-testid="ed-description" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDemandeOpen(false)}>Annuler</Button>
            <Button className="rounded-full bg-primary" onClick={saveDemande} disabled={demandeSaving} data-testid="ed-save">
              {demandeSaving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, full, multiline }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className={`font-medium mt-1 ${multiline ? "whitespace-pre-wrap text-sm" : ""}`}>
        {value || <span className="text-muted-foreground italic font-normal">—</span>}
      </div>
    </div>
  );
}

// Checklist administrative — 4 pré-requis à valider avant de démarrer la proposition.
const ADMIN_CHECKLIST = [
  {
    key: "admin_folder_drive",
    Icon: FolderOpen,
    label: "Dossier manifestation créé dans le Drive",
    hint: "Copier-coller du template dans 0 - En proposition / {année} / {mois} — Nomenclature : AAAAMMJJ - client direct - événement (+ client final si sous-traitance)",
  },
  {
    key: "admin_axonaut_client",
    Icon: Building2,
    label: "Client créé/vérifié sur Axonaut",
    hint: "Rechercher via l'annuaire entreprises Axonaut ; en cas de doute vérifier sur societe.com. Si présent : contrôler la présence du SIREN (facturation électronique).",
  },
  {
    key: "admin_axonaut_contact",
    Icon: UserCheck,
    label: "Contact client créé/vérifié sur Axonaut",
    hint: "Nom / prénom / poste / mail / téléphone. Si déjà présent, vérifier que la fiche est complète et à jour.",
  },
  {
    key: "admin_print_demande",
    Icon: Printer,
    label: "Impression PDF de la demande (+ email si demande par mail) dans 0 - Proposition / Demande du client",
    hint: "Utilisez le bouton « Imprimer la demande » ci-dessus, puis déposez le PDF dans le sous-dossier « Demande du client ».",
  },
];

function AdminChecklistRow({ item, state, onToggle }) {
  const done = !!state?.done;
  const { Icon } = item;
  return (
    <button
      type="button"
      onClick={() => onToggle(item.key, !done)}
      data-testid={`admin-check-${item.key}`}
      className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-all active:scale-[0.99] ${done ? "border-emerald-500/50 bg-emerald-500/15" : "border-rose-500/40 bg-rose-500/15/50 hover:bg-rose-500/15"}`}
    >
      {/* Feu tricolore : vert si fait, rouge si à faire */}
      <div className={`h-4 w-4 rounded-full shrink-0 mt-1 shadow-inner ${done ? "bg-emerald-500 shadow-emerald-700/40" : "bg-rose-500 shadow-rose-700/40 animate-pulse"}`}
           style={{ boxShadow: done ? "0 0 8px rgba(16,185,129,0.6)" : "0 0 8px rgba(244,63,94,0.5)" }}
      />
      <Icon className={`h-4 w-4 shrink-0 mt-1 ${done ? "text-emerald-300" : "text-rose-300"}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${done ? "text-emerald-200" : "text-rose-200"}`}>{item.label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{item.hint}</div>
        {done && state?.date && (
          <div className="text-[10px] text-emerald-300 mt-1 tabular-nums">Validé le {state.date}</div>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold shrink-0 mt-1">
        {done ? <span className="text-emerald-300">FAIT</span> : <span className="text-rose-300">À FAIRE</span>}
      </div>
    </button>
  );
}

// ==== Documents joints à l'événement ====
const DOC_CATEGORIES = [
  { v: "plan",    label: "Plan" },
  { v: "photo",   label: "Photo" },
  { v: "brief",   label: "Brief" },
  { v: "devis",   label: "Devis" },
  { v: "contrat", label: "Contrat" },
  { v: "autre",   label: "Autre" },
];

function DocumentsCard({ event, reload }) {
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("plan");
  const [customName, setCustomName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const docs = event.documents || [];

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!customName.trim()) {
      toast.error("Nom du document requis avant l'upload");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("name", customName.trim());
      await api.post(`/events/${event.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      setCustomName("");
      e.target.value = "";
      await reload(true);
    } catch (err) { toast.error(extractError(err)); }
    finally { setUploading(false); }
  };

  const deleteDoc = async (doc) => {
    if (!confirm(`Supprimer « ${doc.name} » ?`)) return;
    try {
      await api.delete(`/events/${event.id}/documents/${doc.id}`);
      toast.success("Document supprimé");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  const startRename = (doc) => {
    setRenamingId(doc.id);
    setRenameValue(doc.name);
  };
  const commitRename = async (doc) => {
    const v = renameValue.trim();
    if (!v) { toast.error("Nom requis"); return; }
    if (v === doc.name) { setRenamingId(null); return; }
    try {
      await api.patch(`/events/${event.id}/documents/${doc.id}`, { name: v });
      setRenamingId(null);
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";

  return (
    <Card className="border-border/60 shadow-warm" data-testid="documents-card">
      <CardContent className="p-5 md:p-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3 flex items-center gap-1">
          <Paperclip className="h-3 w-3 text-primary" /> Documents joints ({docs.length})
        </div>

        <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3 flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Catégorie</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full h-9 mt-1 rounded-md border border-border bg-background px-2 text-sm"
              data-testid="doc-category"
            >
              {DOC_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Nom du document <span className="text-destructive">*</span></label>
            <input
              type="text"
              value={customName}
              onChange={e => setCustomName(e.target.value)}
              placeholder="Ex : Plan implantation V2"
              className="w-full h-9 mt-1 rounded-md border border-border bg-background px-2 text-sm"
              data-testid="doc-name"
            />
          </div>
          <label className={`inline-flex items-center gap-2 rounded-full ${uploading || !customName.trim() ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-primary text-white active:scale-95 cursor-pointer"} px-4 h-9 text-sm font-semibold whitespace-nowrap`} data-testid="doc-upload-label">
            <Upload className="h-4 w-4" />
            {uploading ? "Envoi…" : "Ajouter un document"}
            <input type="file" className="hidden" onChange={onFileChange} disabled={uploading || !customName.trim()} data-testid="doc-file-input" />
          </label>
        </div>

        <div className="mt-3 divide-y divide-border/60 border border-border/60 rounded-xl overflow-hidden">
          {docs.length === 0 ? (
            <div className="text-sm text-muted-foreground italic text-center py-6">Aucun document pour l'instant — ajoutez plans, photos ou autres pièces jointes.</div>
          ) : docs.map(d => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted" data-testid={`doc-row-${d.id}`}>
              <div className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-primary/10 text-primary border border-primary/30 shrink-0">
                {DOC_CATEGORIES.find(c => c.v === d.category)?.label || "Autre"}
              </div>
              <div className="flex-1 min-w-0">
                {renamingId === d.id ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onBlur={() => commitRename(d)}
                    onKeyDown={e => { if (e.key === "Enter") commitRename(d); if (e.key === "Escape") setRenamingId(null); }}
                    className="w-full h-8 rounded-md border border-primary/50 bg-background px-2 text-sm font-semibold"
                    data-testid={`doc-rename-input-${d.id}`}
                  />
                ) : (
                  <button
                    onClick={() => startRename(d)}
                    className="text-sm font-semibold truncate text-left hover:underline"
                    data-testid={`doc-rename-btn-${d.id}`}
                    title="Cliquer pour renommer"
                  >
                    {d.name || d.original_name}
                    <Pencil className="h-3 w-3 inline-block ml-1 opacity-40" />
                  </button>
                )}
                <div className="text-[11px] text-muted-foreground tabular-nums">
                  {d.original_name}
                  {d.size ? ` · ${Math.round((d.size || 0) / 1024)} Ko` : ""}
                  {d.uploaded_at ? ` · ${d.uploaded_at.slice(0, 10)}` : ""}
                </div>
              </div>
              <a
                href={`${apiBase}/files/${d.file_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-primary inline-flex items-center gap-1 text-xs underline shrink-0"
                data-testid={`doc-view-${d.id}`}
              >
                <Download className="h-3 w-3" /> Ouvrir
              </a>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10 h-8 w-8"
                onClick={() => deleteDoc(d)}
                data-testid={`doc-delete-${d.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
